import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { SetStateAction } from "react";
import type { SortingState, VisibilityState } from "@univdiam/ui/lib/SharedTypes";
import type { User } from "./types";

interface AuthState {
  user: null | User;
  setUser: (user: User) => void;
  notificationCount: number;
  setNotificationCount: (value: number) => void;
  chatUnreadCount: number;
  setChatUnreadCount: (value: number) => void;
  discussionUnreadCount: number;
  setDiscussionUnreadCount: (value: number) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools((set) => ({
    user: null,
    notificationCount: 0,
    chatUnreadCount: 0,
    discussionUnreadCount: 0,
    setNotificationCount: (value: number) => set({ notificationCount: value }),
    setChatUnreadCount: (value: number) => set({ chatUnreadCount: value }),
    setDiscussionUnreadCount: (value: number) => set({ discussionUnreadCount: value }),
    setUser: (user) => set({ user }),
    logout: () => set({ user: null }),
  }))
);

type DashboardState = {
  view: "Kanban" | "Table";
  setView: (view: "Kanban" | "Table") => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  page: number;
  setPage: (value: number) => void;
  tagFilter: string[];
  // Only orders flagged "Order submitted" (request not yet acted on).
  submittedOnly: boolean;
  setSubmittedOnly: (submittedOnly: boolean) => void;
  setTagFilter: (updater: SetStateAction<string[]>) => void;
  sorting: SortingState;
  setSorting: (updater: SetStateAction<SortingState>) => void;
  columnVisibility: VisibilityState;
  setColumnVisibility: (updater: SetStateAction<VisibilityState>) => void;
  kanbanColumnVisibility: Record<string, boolean>;
  setKanbanColumnVisibility: (
    updater: SetStateAction<Record<string, boolean>>
  ) => void;
  clearDashboardFilters: () => void;
};

// lets the store setters accept the React updater-function form, so they can be
// handed straight to tanstack-table's onSortingChange / onColumnVisibilityChange
const applyUpdate = <T>(updater: SetStateAction<T>, prev: T): T =>
  typeof updater === "function" ? (updater as (p: T) => T)(prev) : updater;

// Persisted so filters survive opening an order and coming back, no matter how
// the user gets back (handleBack, sidebar link, logo, reload).
export const useDashboardView = create<DashboardState>()(
  persist(
    (set) => ({
      view: "Kanban",
      setView: (view) => set({ view }),
      searchQuery: "",
      setSearchQuery: (searchQuery) => set({ searchQuery, page: 1 }),
      page: 1,
      setPage: (page) => set({ page }),
      tagFilter: [],
      submittedOnly: false,
      setSubmittedOnly: (submittedOnly) => set({ submittedOnly, page: 1 }),
      setTagFilter: (updater) =>
        set((s) => ({ tagFilter: applyUpdate(updater, s.tagFilter) })),
      sorting: [],
      setSorting: (updater) =>
        set((s) => ({ sorting: applyUpdate(updater, s.sorting) })),
      columnVisibility: {},
      setColumnVisibility: (updater) =>
        set((s) => ({
          columnVisibility: applyUpdate(updater, s.columnVisibility),
        })),
      kanbanColumnVisibility: {},
      setKanbanColumnVisibility: (updater) =>
        set((s) => ({
          kanbanColumnVisibility: applyUpdate(updater, s.kanbanColumnVisibility),
        })),
      clearDashboardFilters: () =>
        set({ searchQuery: "", page: 1, tagFilter: [], submittedOnly: false, sorting: [] }),
    }),
    {
      name: "univdiam.admin.dashboard",
      version: 1,
      partialize: (s) => ({
        view: s.view,
        searchQuery: s.searchQuery,
        page: s.page,
        tagFilter: s.tagFilter,
        submittedOnly: s.submittedOnly,
        sorting: s.sorting,
        columnVisibility: s.columnVisibility,
        kanbanColumnVisibility: s.kanbanColumnVisibility,
      }),
    }
  )
);

export type OrderNavItem = {
  id: string;
  orderRef: string;
  customer_id: string;
};

type OrderNavState = {
  /** Siblings of the order being viewed, in the order the board/table showed them. */
  items: OrderNavItem[];
  /** Where the list came from, e.g. the kanban column title. Shown in the header. */
  label: string;
  setOrderNav: (items: OrderNavItem[], label: string) => void;
  clearOrderNav: () => void;
};

// Snapshot taken when an order is opened, so OrderDetail can step to the next
// sibling without re-deriving the board's filtering/sorting. Persisted so the
// snapshot survives a reload of the detail page.
export const useOrderNav = create<OrderNavState>()(
  persist(
    (set) => ({
      items: [],
      label: "",
      setOrderNav: (items, label) => set({ items, label }),
      clearOrderNav: () => set({ items: [], label: "" }),
    }),
    {
      name: "univdiam.admin.orderNav",
      version: 1,
      partialize: (s) => ({ items: s.items, label: s.label }),
    }
  )
);

interface SessionState {
  sessionExpired: boolean;
  setSessionExpired: (value: boolean) => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  sessionExpired: false,
  setSessionExpired: (value) => set({ sessionExpired: value }),
}));
